/** Enterprise asset taxonomy — stored as string in Asset.type / assetType (additive). */

export const ENTERPRISE_ASSET_TYPES = [
  'SERVER',
  'VIRTUAL_MACHINE',
  'CONTAINER',
  'APPLICATION',
  'WEB_APPLICATION',
  'API_SERVICE',
  'DATABASE',
  'OPERATING_SYSTEM',
  'NETWORK_DEVICE',
  'FIREWALL',
  'ROUTER',
  'SWITCH',
  'LOAD_BALANCER',
  'SECURITY_TOOL',
  'SIEM',
  'EDR',
  'IAM',
  'DLP',
  'WAF',
  'CRYPTO_TOOL',
  'HSM',
  'CERTIFICATE',
  'KEY_MANAGEMENT_SYSTEM',
  'DATA_STORE',
  'FILE_SHARE',
  'CLOUD_RESOURCE',
  'CLOUD_ACCOUNT',
  'STORAGE_BUCKET',
  'INTEGRATION',
  'THIRD_PARTY_SERVICE',
  'WORKSTATION',
  'MOBILE_DEVICE',
  'IOT_DEVICE',
  'OTHER',
  // Legacy API values (preserved)
  'SERVERS',
  'CLOUD',
  'NETWORK',
  'IOT',
  'IP',
  'URL',
  'HOSTNAME',
  'app',
  'web',
  'server',
] as const

export type EnterpriseAssetType = (typeof ENTERPRISE_ASSET_TYPES)[number]

const LEGACY_TYPE_MAP: Record<string, EnterpriseAssetType> = {
  IP: 'SERVER',
  HOSTNAME: 'SERVER',
  URL: 'WEB_APPLICATION',
  app: 'APPLICATION',
  web: 'WEB_APPLICATION',
  server: 'SERVER',
  SERVERS: 'SERVER',
  CLOUD: 'CLOUD_RESOURCE',
  NETWORK: 'NETWORK_DEVICE',
  APPLICATION: 'APPLICATION',
  DATABASE: 'DATABASE',
  IOT: 'IOT_DEVICE',
  OTHER: 'OTHER',
}

const SCANNABLE_TYPES = new Set<string>([
  'SERVER',
  'SERVERS',
  'server',
  'VIRTUAL_MACHINE',
  'CONTAINER',
  'WEB_APPLICATION',
  'web',
  'URL',
  'API_SERVICE',
  'NETWORK_DEVICE',
  'NETWORK',
  'FIREWALL',
  'ROUTER',
  'SWITCH',
  'LOAD_BALANCER',
  'CLOUD_RESOURCE',
  'CLOUD',
  'IP',
  'HOSTNAME',
  'WORKSTATION',
])

export function normalizeAssetType(raw: string | null | undefined): string {
  const s = String(raw || 'OTHER').trim()
  if (!s) return 'OTHER'
  const upper = s.toUpperCase()
  if (ENTERPRISE_ASSET_TYPES.includes(upper as EnterpriseAssetType)) return upper
  if (LEGACY_TYPE_MAP[s]) return LEGACY_TYPE_MAP[s]
  if (LEGACY_TYPE_MAP[upper]) return LEGACY_TYPE_MAP[upper]
  return s
}

export function isScannableAssetType(raw: string | null | undefined): boolean {
  const normalized = normalizeAssetType(raw)
  return SCANNABLE_TYPES.has(normalized) || SCANNABLE_TYPES.has(String(raw || ''))
}

export function assetTypeI18nKey(type: string): string {
  const n = normalizeAssetType(type)
  return `assets.taxonomy.${n}`
}

/** Category views for inventory quick filters (additive). */
export type AssetCategoryView =
  | 'infrastructure'
  | 'applications'
  | 'security'
  | 'dataStores'
  | 'cloud'
  | 'crypto'
  | 'thirdParty'
  | 'endpoints'

export const ASSET_CATEGORY_VIEWS: AssetCategoryView[] = [
  'infrastructure',
  'applications',
  'security',
  'dataStores',
  'cloud',
  'crypto',
  'thirdParty',
  'endpoints',
]

const CATEGORY_TYPE_MAP: Record<AssetCategoryView, string[]> = {
  infrastructure: [
    'SERVER', 'VIRTUAL_MACHINE', 'CONTAINER', 'NETWORK_DEVICE', 'FIREWALL', 'ROUTER', 'SWITCH',
    'LOAD_BALANCER', 'OPERATING_SYSTEM', 'SERVERS', 'NETWORK', 'IP', 'HOSTNAME', 'server',
  ],
  applications: ['APPLICATION', 'WEB_APPLICATION', 'API_SERVICE', 'INTEGRATION', 'app', 'web', 'URL'],
  security: ['SECURITY_TOOL', 'SIEM', 'EDR', 'IAM', 'DLP', 'WAF'],
  dataStores: ['DATABASE', 'DATA_STORE', 'FILE_SHARE'],
  cloud: ['CLOUD_RESOURCE', 'CLOUD_ACCOUNT', 'STORAGE_BUCKET', 'CLOUD'],
  crypto: ['CRYPTO_TOOL', 'HSM', 'CERTIFICATE', 'KEY_MANAGEMENT_SYSTEM'],
  thirdParty: ['THIRD_PARTY_SERVICE'],
  endpoints: ['WORKSTATION', 'MOBILE_DEVICE', 'IOT_DEVICE', 'IOT'],
}

export function assetCategoryI18nKey(view: AssetCategoryView): string {
  return `assets.categoryViews.${view}`
}

export function assetMatchesCategoryView(type: string | null | undefined, view: AssetCategoryView): boolean {
  const normalized = normalizeAssetType(type)
  const allowed = CATEGORY_TYPE_MAP[view] || []
  return allowed.includes(normalized) || allowed.includes(String(type || '').toUpperCase())
}

/** Narrow available asset types when a category view is active (enterprise inventory filters). */
export function filterTypesForCategoryView(types: string[], view: AssetCategoryView | ''): string[] {
  if (!view) return types
  return types.filter((type) => assetMatchesCategoryView(type, view))
}

export function assetTypeOptions(t: (key: string, fallback?: string) => string) {
  const canonical = [
    'SERVER',
    'VIRTUAL_MACHINE',
    'CONTAINER',
    'APPLICATION',
    'WEB_APPLICATION',
    'API_SERVICE',
    'DATABASE',
    'OPERATING_SYSTEM',
    'NETWORK_DEVICE',
    'FIREWALL',
    'ROUTER',
    'SWITCH',
    'LOAD_BALANCER',
    'SECURITY_TOOL',
    'SIEM',
    'EDR',
    'IAM',
    'DLP',
    'WAF',
    'CRYPTO_TOOL',
    'HSM',
    'CERTIFICATE',
    'KEY_MANAGEMENT_SYSTEM',
    'DATA_STORE',
    'FILE_SHARE',
    'CLOUD_RESOURCE',
    'CLOUD_ACCOUNT',
    'STORAGE_BUCKET',
    'INTEGRATION',
    'THIRD_PARTY_SERVICE',
    'WORKSTATION',
    'MOBILE_DEVICE',
    'IOT_DEVICE',
    'OTHER',
  ]
  return canonical.map((value) => ({
    value,
    label: t(assetTypeI18nKey(value), value.replace(/_/g, ' ')),
  }))
}
